import React from "react";
import { GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import { AttachmentForm } from "../../../Attachments/AttachmentForm";
import { CustomImportForm } from "../../../CustomImports/CustomImportForm";
import { ProjectForm } from "../../../Projects/ProjectForm";
import { CustomTypedEntityFormProps, EntityFormProps } from "../../../common/entity/EntityInterfaces";
import { AnnouncementForm } from "../../../Announcements/AnnouncementForm";
import { BridgeForm } from "../../../Autoloads/Bridges/BridgeForm";
import { DataSourceForm } from "../../../Autoloads/DataSources/DataSourceForm";
import { DatasetForm } from "../../../Dataset/Form/DatasetForm";
import { InventoryItemForm } from "../../../Inventories/InventoryItemForm";
import { PersonForm } from "../../../Persons/PersonForm";
import { RoleForm } from "../../../Roles/RoleForm";
import { SampleForm } from "../../../samples/SampleForm";
import { SharedContentForm } from "../../../SharedBundles/SharedContentForm";
import { NotAvailable } from "../../../common/misc/UIconstants";
import { CustomFieldsForm } from "../../../Customization/CustomFields/CustomFieldsForm";
import { CustomTypesForm } from "../../../Customization/CustomTypes/CustomTypeForm";
import { LabNotebookForm } from "../../../ELN/LabNotebooks/Forms/LabNotebookForm";
import { LabNotebookExperimentForm } from "../../../ELN/LabNotebookExperiments/Forms/LabNotebookExperimentForm";
import { LabNotebookEntryForm } from "../../../ELN/LabNotebookEntries/Forms/LabNotebookEntryForm";
import { LabNotebookTemplateForm } from "../../../ELN/LabNotebookTemplates/LabNotebookTemplateForm";

// Map each key in genericEntityConstants to its specific props type
type EntityFormPropsByKey<K extends GenericEntityConstantsEntities> = K extends "samples"
  ? CustomTypedEntityFormProps<"samples">
  : K extends "announcements"
  ? EntityFormProps<"announcements">
  : K extends "apiKeys"
  ? EntityFormProps<"apiKeys">
  : K extends "attachments"
  ? EntityFormProps<"attachments">
  : K extends "auditLogs"
  ? EntityFormProps<"auditLogs">
  : K extends "bridges"
  ? EntityFormProps<"bridges">
  : K extends "customFields"
  ? EntityFormProps<"customFields">
  : K extends "customImports"
  ? EntityFormProps<"customImports">
  : K extends "customImportLogs"
  ? EntityFormProps<"customImportLogs">
  : K extends "customTypeTemplates"
  ? EntityFormProps<"customTypeTemplates">
  : K extends "customFieldTemplates"
  ? EntityFormProps<"customFieldTemplates">
  : K extends "customTypeTemplatePackages"
  ? EntityFormProps<"customTypeTemplatePackages">
  : K extends "customTypes"
  ? EntityFormProps<"customTypes">
  : K extends "datasets"
  ? CustomTypedEntityFormProps<"datasets">
  : K extends "dataFormats"
  ? EntityFormProps<"dataFormats">
  : K extends "dataSources"
  ? EntityFormProps<"dataSources">
  : K extends "dataSourceStatus"
  ? EntityFormProps<"dataSourceStatus">
  : K extends "fileExportJobs"
  ? EntityFormProps<"fileExportJobs">
  : K extends "iconNMRHistoryEntry"
  ? EntityFormProps<"iconNMRHistoryEntry">
  : K extends "iconNMRHolders"
  ? EntityFormProps<"iconNMRHolders">
  : K extends "inventories"
  ? EntityFormProps<"inventories">
  : K extends "inventoryItems"
  ? CustomTypedEntityFormProps<"inventoryItems">
  : K extends "notebooks"
  ? EntityFormProps<"notebooks">
  : K extends "notebookExperiments"
  ? EntityFormProps<"notebookExperiments">
  : K extends "notebookEntries"
  ? EntityFormProps<"notebookEntries">
  : K extends "notebookTemplates"
  ? EntityFormProps<"notebookTemplates">
  : K extends "methods"
  ? EntityFormProps<"methods">
  : K extends "persons"
  ? CustomTypedEntityFormProps<"persons">
  : K extends "projects"
  ? CustomTypedEntityFormProps<"projects">
  : K extends "roles"
  ? EntityFormProps<"roles">
  : K extends "samples"
  ? CustomTypedEntityFormProps<"samples">
  : K extends "sharedContents"
  ? EntityFormProps<"sharedContents">
  : K extends "vendors"
  ? EntityFormProps<"vendors">
  : never;

// type AllEntityFormProps = {
//   [EntityTypeId in GenericEntityConstantsEntities]: EntityFormPropsByKey<EntityTypeId>;
// };

interface GenericEntityFormProps<EntityTypeId extends GenericEntityConstantsEntities> {
  entityTypeId: EntityTypeId;
  props: EntityFormPropsByKey<EntityTypeId>;
}

export const GenericEntityForm = <EntityTypeId extends GenericEntityConstantsEntities>({
  entityTypeId,
  props,
}: GenericEntityFormProps<EntityTypeId>) => {
  switch (entityTypeId) {
    case "announcements":
      return <AnnouncementForm {...(props as any)} />;
    case "apiKeys":
      return NotAvailable;
    case "attachments":
      return <AttachmentForm {...(props as any)} />;
    case "auditLogs":
      return NotAvailable;
    case "bridges":
      return <BridgeForm {...(props as any)} />;
    case "customFields":
      return <CustomFieldsForm {...(props as any)} />;
    case "customImports":
      return <CustomImportForm {...(props as any)} />;
    case "customImportLogs":
      return NotAvailable;
    case "customTypeTemplates":
      return NotAvailable;
    case "customFieldTemplates":
      return NotAvailable;
    case "customTypeTemplatePackages":
      return NotAvailable;
    case "customTypes":
      return <CustomTypesForm {...(props as any)} />;
    case "datasets":
      return <DatasetForm {...(props as any)} />;
    case "dataFormats":
      return NotAvailable;
    case "dataSources":
      return <DataSourceForm {...(props as any)} />;
    case "dataSourceStatus":
      return NotAvailable;
    case "fileExportJobs":
      return NotAvailable;
    case "iconNMRHistoryEntry":
      return NotAvailable;
    case "iconNMRHolders":
      return NotAvailable;
    case "inventories":
      return <CustomTypesForm {...(props as any)} />;
    case "inventoryItems":
      return <InventoryItemForm {...(props as any)} />;
    case "notebooks":
      return <LabNotebookForm {...(props as any)} />;
    case "notebookExperiments":
      return <LabNotebookExperimentForm {...(props as any)} />;
    case "notebookEntries":
      return <LabNotebookEntryForm {...(props as any)} />;
    case "notebookTemplates":
      return <LabNotebookTemplateForm {...(props as any)} />;
    case "methods":
      return NotAvailable;
    case "persons":
      return <PersonForm {...(props as any)} />;
    case "projects":
      return <ProjectForm {...(props as any)} />;
    case "roles":
      return <RoleForm {...(props as any)} />;
    case "samples":
      return <SampleForm {...(props as any)} />;
    case "sharedContents":
      return <SharedContentForm {...(props as any)} />;
    case "vendors":
      return NotAvailable;
    default:
      return NotAvailable;
  }
};
